import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useState } from 'react'
import { Helmet } from 'react-helmet'
import { useLocation, useNavigate } from 'react-router'

import { OnboardingBannerSlot } from '@/components/OnboardingBannerSlot'
import { QuotesLockNotice } from '@/components/QuotesLockNotice'
import { Button } from '@/components/ui/button'
import { ResponsiveTable, type ResponsiveTableColumn } from '@/components/ui/responsive-table'
import { SkeletonRows } from '@/components/ui/skeleton'
import { UploadBlankState } from '@/components/ui/upload-blank-state'
import { type AccidentViolationItem, useAccidentsViolations } from '@/models/dashboard'
import { continuesApplication, showsQuotesLock, useCurrentUser } from '@/models/user'
import {
  categoryLabel,
  formatAmount,
  formatEventDate,
  referenceLabel,
  SOURCE_LABELS,
} from './violationDisplay'

/** The date leads the row and titles the card — a reference can be missing, a date reads as the event. */
function buildColumns(showSource: boolean): ResponsiveTableColumn<AccidentViolationItem>[] {
  return [
    {
      key: 'date',
      header: 'Date',
      cellClassName: 'font-medium',
      hideOnCard: true,
      cell: (item) => formatEventDate(item.date),
    },
    {
      key: 'reference',
      header: showSource ? 'Reference' : 'Claim #',
      cell: (item) => <span className="capitalize">{referenceLabel(item)}</span>,
    },
    {
      key: 'category',
      header: showSource ? 'Coverage / type' : 'Coverage',
      cell: (item) => (
        <span className="capitalize">
          {categoryLabel(item)}
          {(item.claimCount ?? 1) > 1 && <span className="text-muted-foreground"> ×{item.claimCount}</span>}
        </span>
      ),
    },
    ...(showSource
      ? [
          {
            key: 'source',
            header: 'Source',
            cell: (item: AccidentViolationItem) => SOURCE_LABELS[item.source],
          },
        ]
      : []),
    { key: 'driver', header: 'Driver', cell: (item) => item.driverName || '—' },
    { key: 'insurer', header: 'Insurer', cell: (item) => item.insurer || '—' },
    {
      key: 'paidLoss',
      header: 'Paid',
      headClassName: 'text-right',
      cellClassName: 'text-right',
      cell: (item) => formatAmount(item.paidLoss),
    },
    {
      key: 'reserve',
      header: 'Reserve',
      headClassName: 'text-right',
      cellClassName: 'text-right',
      cell: (item) => formatAmount(item.reserve),
    },
    {
      key: 'totalIncurred',
      header: 'Total incurred',
      headClassName: 'text-right',
      cellClassName: 'text-right font-medium',
      cell: (item) => formatAmount(item.totalIncurred),
    },
  ]
}

export function Violations() {
  const { user } = useCurrentUser({ enabled: true })
  const location = useLocation()
  const initialPage = (location.state as { page?: number })?.page ?? 1
  const [page, setPage] = useState(initialPage)
  const { items, dataSources, totalPages, isLoading } = useAccidentsViolations(page)
  const navigate = useNavigate()

  // Loss-run claims are always served; FMCSA/telematics rows only show up when
  // their flag is on, and then the feed is mixed and needs a Source column.
  // Read it off dataSources rather than the current page's rows so the column
  // doesn't appear and disappear as you page through.
  const showSource = !!(dataSources?.cab || dataSources?.terminal)

  return (
    <>
      <Helmet>
        <title>Loss runs</title>
      </Helmet>

      <div className="mb-4">
        <h1 className="text-ink text-2xl font-bold tracking-[-0.01em]">Loss runs</h1>
        {showSource && (
          <p className="text-muted-foreground text-sm">
            Claims from your loss runs, plus your federal and telematics safety record.
          </p>
        )}
      </div>
      {/* Placed while loading too, or the layout draws the banner above the title until data lands. */}
      <OnboardingBannerSlot className="mb-4" />

      {showsQuotesLock(user, user?.currentOrg.currentInsuranceApplication) && (
        <div className="mb-6">
          <QuotesLockNotice section="claims-history" />
        </div>
      )}

      {isLoading ? (
        <SkeletonRows />
      ) : items.length === 0 && continuesApplication(user) ? (
        // Loss runs come in through the application, so the empty state shows that path disabled.
        <UploadBlankState addLabel="Add loss run" onAdd={() => {}} onFile={() => {}} disabled />
      ) : items.length === 0 ? (
        <div className="mt-10 mb-10 flex w-full flex-col items-center justify-center">
          <p className="text-muted-foreground text-sm">No claims on file from your loss runs</p>
        </div>
      ) : (
        <>
          <ResponsiveTable
            items={items}
            columns={buildColumns(showSource)}
            getRowId={(item) => item.id}
            cardTitle={(item) => formatEventDate(item.date)}
            onRowClick={(item) =>
              navigate(`/insure/violations/${encodeURIComponent(item.id)}`, {
                state: { item, page },
              })
            }
          />

          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <span className="text-muted-foreground text-xs">
                Page {page} of {totalPages}
              </span>
              <div className="flex gap-1">
                <Button
                  variant="outline"
                  size="icon"
                  className="size-7"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  <ChevronLeft className="size-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="size-7"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  <ChevronRight className="size-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </>
  )
}
